import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, UserPlus, Trash2, Mail, ShieldCheck, Send } from "lucide-react";
import * as api from "../../lib/crmApi";
import {
    adminListUsers,
    adminCreateUser,
    adminDeleteUser,
    adminTestDailyDigest,
    adminMe,
    formatApiError,
} from "../../lib/adminApi";
import StagesSection from "./StagesSection";

export default function CrmSettings() {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => { api.getSettings().then(setS); }, []);

    if (!s) return <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

    const save = async () => {
        setSaving(true);
        try { const upd = await api.putSettings(s); setS(upd); toast.success("Indstillinger gemt"); }
        catch { toast.error("Fejl"); } finally { setSaving(false); }
    };

    const Field = ({ k, label, help }) => (
        <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
            <input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })}
                data-testid={`crm-settings-${k}`}
                className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm" />
            {help && <p className="mt-1 text-xs text-slate-500">{help}</p>}
        </div>
    );

    return (
        <div className="p-6 lg:p-8 max-w-3xl space-y-8" data-testid="crm-settings">
            <h1 className="font-display text-3xl font-bold text-slate-900">Indstillinger</h1>

            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
                <h2 className="text-lg font-semibold text-slate-900">E-mail & sælger</h2>
                <Field k="signature_name" label="Afsendernavn" help="Vises som navn i mailboksen. F.eks. 'Cleany Support'" />
                <Field k="sender_email" label="Afsender e-mail" help="Skal være verificeret i Resend" />
                <Field k="reply_to" label="Reply-to e-mail" help="Hvor kunder svarer hen. Standard: info@cleany.dk" />
                <Field k="seller_name" label="Ansvarlig sælger (navn)" />
                <Field k="seller_email" label="Sælger e-mail" help="Vises i {{sælger_email}} token" />
                <Field k="seller_phone" label="Sælger telefon" help="Vises i {{sælger_telefon}} token" />
                <Field k="booking_link" label="Booking-link" help="Vises i {{booking_link}} token" />
                <div className="pt-4 border-t border-slate-200 flex justify-end">
                    <button onClick={save} disabled={saving} data-testid="crm-settings-save"
                        className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 h-10">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Gem indstillinger
                    </button>
                </div>
            </div>

            <SalesRepsSection />

            <StagesSection />

            <DailyDigestSection />
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────
 * Sales reps management — visible ONLY to super_admin.
 * ─────────────────────────────────────────────────────────── */
function SalesRepsSection() {
    const [me, setMe] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const meData = await adminMe();
            setMe(meData);
            if (meData.role === "super_admin") {
                setUsers(await adminListUsers());
            }
        } catch (e) {
            console.warn(e);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    if (loading) return null;
    if (!me || me.role !== "super_admin") return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email || !form.password || !form.name) {
            toast.error("Alle felter er påkrævet");
            return;
        }
        if (form.password.length < 8) {
            toast.error("Adgangskoden skal være mindst 8 tegn");
            return;
        }
        setCreating(true);
        try {
            await adminCreateUser(form);
            toast.success("Sælger oprettet");
            setForm({ name: "", email: "", password: "" });
            setUsers(await adminListUsers());
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setCreating(false); }
    };

    const remove = async (u) => {
        if (u.role === "super_admin") return;
        if (!window.confirm(`Slet sælger ${u.email}?`)) return;
        try {
            await adminDeleteUser(u.id);
            toast.success("Sælger slettet");
            setUsers(await adminListUsers());
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="crm-settings-users">
            <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-sky-600" />
                <h2 className="text-lg font-semibold text-slate-900">Sælger-logins</h2>
            </div>
            <p className="text-sm text-slate-600">
                Opret et unikt login til hver sælger. Alle sælgere modtager den daglige opfølgnings-mail kl. 08:00.
            </p>

            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                {users.length === 0 && (
                    <div className="p-4 text-sm text-slate-500">Ingen brugere fundet.</div>
                )}
                {users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3" data-testid={`user-row-${u.email}`}>
                        <div>
                            <div className="text-sm font-semibold text-slate-900">
                                {u.name}
                                {u.role === "super_admin" && (
                                    <span className="ml-2 inline-flex items-center rounded-full bg-sky-50 text-sky-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                                        Super-admin
                                    </span>
                                )}
                                {u.role === "sales_rep" && (
                                    <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold px-2 py-0.5">
                                        Sælger
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-slate-500">{u.email}</div>
                        </div>
                        {u.role !== "super_admin" && (
                            <button onClick={() => remove(u)}
                                data-testid={`delete-user-${u.email}`}
                                className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700">
                                <Trash2 className="h-3.5 w-3.5" /> Slet
                            </button>
                        )}
                    </div>
                ))}
            </div>

            <form onSubmit={submit} className="pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-3">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Fulde navn"
                    data-testid="new-user-name"
                    className="rounded-lg border border-slate-300 h-10 px-3 text-sm" />
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="e-mail" type="email"
                    data-testid="new-user-email"
                    className="rounded-lg border border-slate-300 h-10 px-3 text-sm" />
                <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Adgangskode (min. 8 tegn)" type="text"
                    data-testid="new-user-password"
                    className="rounded-lg border border-slate-300 h-10 px-3 text-sm" />
                <div className="md:col-span-3 flex justify-end">
                    <button type="submit" disabled={creating}
                        data-testid="create-user-btn"
                        className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 h-10 disabled:opacity-60">
                        {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Opret sælger
                    </button>
                </div>
            </form>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────
 * Daily follow-up digest — send test.
 * ─────────────────────────────────────────────────────────── */
function DailyDigestSection() {
    const [sending, setSending] = useState(false);
    const [me, setMe] = useState(null);
    useEffect(() => { adminMe().then(setMe).catch(() => { }); }, []);
    if (!me || me.role !== "super_admin") return null;

    const trigger = async () => {
        setSending(true);
        try {
            await adminTestDailyDigest();
            toast.success("Daglig opfølgnings-mail sendt til alle sælgere");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setSending(false); }
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3" data-testid="crm-settings-digest">
            <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-sky-600" />
                <h2 className="text-lg font-semibold text-slate-900">Daglig opfølgnings-mail</h2>
            </div>
            <p className="text-sm text-slate-600">
                Sendes automatisk hver morgen kl. 08:00 (Europe/Copenhagen) til alle sælger-logins. Mailen viser dagens
                opfølgninger fra CRM's kalender. Tryk nedenfor for at sende en test nu.
            </p>
            <div className="flex justify-end">
                <button onClick={trigger} disabled={sending}
                    data-testid="test-digest-btn"
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 h-10 disabled:opacity-60">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test-digest nu
                </button>
            </div>
        </div>
    );
}
